'use client';

import React, { useMemo, useEffect } from 'react';
import {
  Card,
  CardContent,
  MedBubbleChart,
  Typography,
  ProfileCardHeader,
  Box,
  CircularProgress,
} from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightLong,
  faChartScatterBubble,
} from '@fortawesome/pro-solid-svg-icons';
import useGetPayerMixReimbursement from 'src/hooks/payerMix/useGetPayerMixReimbursement';
import useNewGetPayerMix from 'src/hooks/payerMix/useNewGetPayerMix';
import useProfileSelections from 'src/hooks/profiles/useProfileSelections';
import useUserSettings from 'src/hooks/users/useUserSettings';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useParams, usePathname } from 'next/navigation';
import Link from 'next/link';
import { REIMBURSEMENT_DATA_YEAR_STR } from 'src/components/constants';

interface NewPayerMixProps {
  showLegend?: boolean;
  singleColumn?: boolean;
  location?: string;
}

const NewPayerMix = ({
  showLegend = false,
  singleColumn = false,
  location,
}: NewPayerMixProps) => {
  const { type } = useParams<{ type: string }>();
  const pathname = usePathname();

  const { entityDetails } = useProfile();
  // An explicit location wins over the route param: in the profile drawer the
  // URL belongs to the host page, not to the entity being previewed, so
  // trusting `type` would read another entity type's code-set selection.
  const typeLocation = location || type || 'hcp';

  const { selectedFilterSet } = useProfileSelections(typeLocation);
  const { settings: userSettings, updateSettings } = useUserSettings();
  const providerType = location || type || entityDetails?.type;

  const { data: reimbursementData } = useGetPayerMixReimbursement({
    providerId: entityDetails?.provider_id,
    location: providerType,
  });

  const newHref = `${pathname.replace(/\/$/, '')}/payer-mix`;

  const { data: payerMixByType, isValidating: isLoadingByType } =
    useNewGetPayerMix({
      providerId: entityDetails?.id,
      groupBy: 'payer_type',
      location: providerType,
    });

  const { data: payerMixByName, isValidating: isLoadingByName } =
    useNewGetPayerMix({
      providerId: entityDetails?.id,
      groupBy: 'payer_name',
      location: providerType,
    });

  const formattedPayerMixData = useMemo(() => {
    if (!payerMixByType?.results?.length) return [];

    return payerMixByType?.results?.map((item) => ({
      name: item.payer_type,
      value: item.percentage,
    }));
  }, [payerMixByType]);

  const sortedPayerResults = useMemo(() => {
    if (!payerMixByName?.results?.length) return [];

    return [...(payerMixByName?.results || [])].sort(
      (a, b) => b.percentage - a.percentage
    );
  }, [payerMixByName]);

  // Validate payer/site combination and fix invalid states
  const payerSiteValidation = useMemo(() => {
    if (!reimbursementData?.results?.length) {
      return { isValid: false, hasData: false };
    }

    const { selectedPayer, selectedSite } = userSettings || {};

    if (!selectedPayer || !selectedSite) {
      return { isValid: false, hasData: true, needsSelection: true };
    }

    // Check if the payer/site combination exists in the data
    const combinationExists = reimbursementData.results.some((result: any) =>
      result.payers.some(
        (payer: any) =>
          payer.payer_name?.toLowerCase() ===
            selectedPayer.payer_name?.toLowerCase() &&
          payer.soc_type === selectedSite
      )
    );

    return {
      isValid: combinationExists,
      hasData: true,
      needsSelection: false,
      selectedPayer,
      selectedSite,
    };
  }, [reimbursementData?.results, userSettings]);

  // Auto-fix invalid site selection based on available data
  useEffect(() => {
    if (
      !payerSiteValidation.hasData ||
      payerSiteValidation.isValid ||
      payerSiteValidation.needsSelection
    ) {
      return;
    }

    // If payer/site combination is invalid, try to find a valid site for the selected payer
    const validSiteForPayer = reimbursementData?.results
      ?.find((result: any) =>
        result.payers.some(
          (payer: any) =>
            payer.payer_name?.toLowerCase() ===
            payerSiteValidation.selectedPayer?.payer_name?.toLowerCase()
        )
      )
      ?.payers?.find(
        (payer: any) =>
          payer.payer_name?.toLowerCase() ===
          payerSiteValidation.selectedPayer?.payer_name?.toLowerCase()
      )?.soc_type;

    if (validSiteForPayer) {
      updateSettings({
        selectedSite: validSiteForPayer,
      });
    }
  }, [payerSiteValidation, reimbursementData?.results, updateSettings]);

  const hasValues =
    payerMixByType?.results?.length > 0 &&
    payerMixByType?.results?.some((item: any) => item.percentage > 0);

  const isICD = selectedFilterSet?.detailed_filters?.some((filter) =>
    filter.type.toLowerCase().includes('icd')
  );

  const currentValue = useMemo(() => {
    const hasValue = !!selectedFilterSet;
    if (!hasValue) return '- All Claims';

    const name = selectedFilterSet?.label;
    return `- ${name} Claims`;
  }, [selectedFilterSet]);

  const hasSelectedValue = useMemo(() => {
    return !!selectedFilterSet;
  }, [selectedFilterSet]);

  const isLoading = isLoadingByType || isLoadingByName;

  return (
    <Card className="h-full flex flex-col p-6 bg-white w-full border border-neutral relative">
      <ProfileCardHeader
        icon={faChartScatterBubble}
        title="Payer Mix"
        hasSelectedSavedSearch={hasSelectedValue}
        currentSavedSearch={currentValue}
        subHeader={`All Payer Claims ${REIMBURSEMENT_DATA_YEAR_STR}`}
      />

      <CardContent className="flex-1 gap-2 overflow-hidden">
        {hasValues ? (
          <Box
            className={`flex flex-col ${
              singleColumn ? '' : 'lg:flex-row'
            } gap-2 h-full justify-center`}
          >
            <Box
              className={`${
                showLegend ? 'lg:w-2/3' : 'w-full'
              } overflow-hidden max-h-96`}
            >
              <MedBubbleChart
                data={formattedPayerMixData}
                width={singleColumn ? 400 : undefined}
              />
            </Box>
            {showLegend && (
              <Box className="flex flex-col justify-center gap-2 h-full w-full lg:w-1/3">
                <Typography className="text-neutral-800 font-bold text-sm leading-5">
                  Top Payers by Name
                </Typography>
                <Box className="py-1 w-full">
                  <Box className="flex gap-2 justify-between">
                    <Typography className="text-neutral-600 font-bold text-xs leading-4">
                      Payer
                    </Typography>
                    <Typography className="text-neutral-600 font-bold text-xs leading-4">
                      % of Claims
                    </Typography>
                  </Box>
                  {sortedPayerResults
                    ?.slice(0, 3)
                    .map((item: any, index: number) => (
                      <Box
                        key={`${item.payer_name}-${item.payer_type}-${index}`}
                        className="flex gap-2 justify-between w-full py-1"
                      >
                        <Typography className="text-neutral-800 text-sm leading-5">
                          {item.payer_name}
                        </Typography>
                        <Typography className="text-neutral-800 text-sm leading-5">
                          {Math.round(item.percentage)}%
                        </Typography>
                      </Box>
                    ))}
                </Box>
                <Link
                  id={`payer-mix-view-all-payers-${entityDetails?.provider_id}`}
                  href={newHref}
                  className="flex flex-row items-center gap-2"
                  rel="noopener noreferrer"
                  data-testid="payer-mix-view-all-payers"
                  scroll={false}
                >
                  <Typography className="text-sky-900 text-sm font-semibold leading-5">
                    View All Payers
                  </Typography>
                  <Icon icon={faArrowRightLong} className="text-sky-900" />
                </Link>
              </Box>
            )}
          </Box>
        ) : !hasValues && isICD ? (
          <Box className="flex items-center justify-center h-full min-h-48">
            <Typography className="text-sm font-semibold max-w-96 text-center text-neutral-500">
              Payer mix is currently unavailable for diagnosis codes. We are
              working on adding support for this soon.
            </Typography>
          </Box>
        ) : (
          <div className="flex items-center justify-center h-full min-h-48">
            <p className="text-sm font-semibold text-neutral-500">
              No data available
            </p>
          </div>
        )}
      </CardContent>
      {isLoading && (
        <Box className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center rounded-lg z-10">
          <CircularProgress size="sm" color="neutral" />
        </Box>
      )}
    </Card>
  );
};

export default NewPayerMix;
